import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { config } from '@accounts/config';
import { Legal, type Product, type ProductHoldings } from '@accounts/domain';
import { lines } from '../organization/holdings';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Consequence, EmptyState, Field, Waiting } from '@beyond-js/ui/react';
import { Panel, Problem, Unsent, useUnread } from '@accounts/ui';

/**
 * What a person may do about their own data: take a copy, or delete the account.
 *
 * Deleting is kept one deliberate step away. The explanation of what it does and does not do is shown
 * before the confirmation, and the confirmation is a word typed in the person's own language, so the
 * button cannot be reached by a stray click or a hurried tab. Both actions need a recent sign-in; when
 * the service asks for one, this says so and offers the way to get it, rather than a generic failure.
 */
export function Data() {
	const { text, identity, accounts, language, settle } = usePresence();

	const [busy, setBusy] = useState(false);
	const [asking, setAsking] = useState(false);
	const [typed, setTyped] = useState('');
	const [failure, setFailure] = useState<string>();
	// An outage is unavailable, never a failure (D40, D132, D133): drawn in the warning tone
	const [outage, setOutage] = useState(false);
	const [notice, setNotice] = useState<string>();
	const [reauth, setReauth] = useState(false);
	// The deletion met an ended session and was not sent: the person presses it again
	const [unsent, setUnsent] = useState(false);
	// What each product holds, and the organizations that block the deletion, read when it is opened (ACC-08);
	// why that could not be read is `unheld`, which never reads as nothing being held
	const [held, setHeld] = useState<{ holdings: ProductHoldings[]; blocking: { id: string; name: string }[] }>();
	const [unheld, setUnheld] = useState<Refusal>();
	const [products, setProducts] = useState<Product[]>();

	// Opening the deletion moves the focus to its heading, and cancelling returns it to the button that
	// opened it, because the one replaces the other and the focus would otherwise fall to the page
	const heading = useRef<HTMLHeadingElement>(null);
	const opener = useRef<HTMLDivElement>(null);
	const confirmation = useRef<HTMLInputElement>(null);
	const opened = useRef(false);
	// Whether Accounts answered, which takes the read out of the page's outage while the form is open (D163)
	const holdings = useCallback(() => Refusal.answered(async () => setHeld(await accounts.holdings()), setUnheld), [accounts]);
	const unanswered = useUnread({ key: 'holdings', title: text('outage.holdings'), unread: unheld, run: holdings }, asking);
	useEffect(() => {
		if (!asking) return;
		void holdings();
		accounts.providers().then(found => setProducts(found.products), () => undefined);
	}, [asking, accounts, holdings]);
	useEffect(() => {
		if (asking) heading.current?.focus();
		else if (opened.current) opener.current?.querySelector('button')?.focus();
		opened.current = asking;
	}, [asking]);

	const policy = new Legal(config.legal).address('privacy', language);
	const word = text('data.erase.word');

	function refused(error: unknown) {
		// Withheld by the page's session, which answers the person: no failure of this panel's own
		if (Refusal.is(error) && error.quiet) return setUnsent(error.unsent);
		if (Refusal.is(error) && error.code === 'REAUTHENTICATION_REQUIRED') {
			setReauth(true);
			setOutage(false);
			setFailure(text('data.reauth'));
			return;
		}
		if (Refusal.is(error) && error.code === 'LAST_OWNER') {
			// Said beside the action, naming each organization with a link to it (ACC-08)
			const organizations = (error.details?.organizations as { id: string; name: string }[] | undefined) ?? [];
			setHeld(current => ({ holdings: current?.holdings ?? [], blocking: organizations }));
			return;
		}
		setOutage(Refusal.is(error) && error.unreachable);
		setFailure(Refusal.is(error) ? text(error.key) : text('error.unexpected'));
	}

	async function copy() {
		setBusy(true);
		setFailure(undefined);
		setNotice(undefined);
		try {
			const document = await accounts.copy();
			const file = new Blob([JSON.stringify(document, null, 2)], { type: 'application/json' });
			const address = URL.createObjectURL(file);
			const anchor = globalThis.document.createElement('a');
			anchor.href = address;
			anchor.download = `beyond-accounts-${identity?.account.id ?? 'export'}.json`;
			anchor.click();
			URL.revokeObjectURL(address);
			setNotice(text('data.copied'));
		} catch (error) {
			refused(error);
		} finally {
			setBusy(false);
		}
	}

	async function erase() {
		if (!identity || typed.trim().toUpperCase() !== word.toUpperCase() || (held === undefined && !unheld) || held?.blocking.length) return;
		setBusy(true);
		setFailure(undefined);
		setUnsent(false);
		try {
			await accounts.erase(identity.account.id);
			// Every session ended: a full load, not a route change, so the sign-in screen says when it
			// takes effect and how to keep it
			globalThis.location.replace('/signin?erased=1');
		} catch (error) {
			refused(error);
			// Nothing came back, so the account may already be erased: standing is read again, which
			// leads to the sign-in screen if it was
			if (Refusal.is(error) && error.unknown) await settle();
		} finally {
			setBusy(false);
		}
	}

	async function again() {
		// Signing out of this surface is what makes the sign-in screen appear; the person comes back here
		await accounts.leave().catch(() => undefined);
		await settle();
	}

	return (
		<Panel title={text('data.title')} description={text('data.lead')}>
			<div className="flex flex-col gap-4">
				{failure ? (
					<Callout tone={outage ? 'warning' : 'danger'} live title={failure}>
						{reauth ? (
							<Button variant="secondary" onClick={() => void again()}>
								{text('data.reauth.action')}
							</Button>
						) : null}
					</Callout>
				) : null}
				{notice ? <Callout tone="success" live title={notice} /> : null}

				<div className="flex flex-col gap-2">
					<p className="max-w-reading text-body text-ink-muted">{text('data.copy.lead')}</p>
					<div className="flex flex-wrap gap-2">
						<Button variant="secondary" busy={busy && !asking} labels={{ busy: text('state.loading') }} onClick={() => void copy()}>
							{text('data.copy')}
						</Button>
						{policy ? (
							<a className="inline-flex min-h-[44px] items-center text-body text-ink underline" href={policy} rel="noopener">
								{text('data.policy')}
							</a>
						) : null}
					</div>
				</div>

				<div className="flex flex-col gap-3 border-t border-line pt-4">
					{asking ? (
						<form
							className="flex max-w-reading flex-col gap-3"
							onSubmit={event => {
								event.preventDefault();
								void erase();
							}}
						>
							<h3 ref={heading} tabIndex={-1} className="text-title-3 text-ink">
								{text('data.erase.title')}
							</h3>
							<p className="text-body text-ink-muted">{text('data.erase.lead')}</p>
							{/* The consequence before the confirmation, drawn by the shared confirmation's own builder (D17) */}
							{/* What the products hold is read as the form opens; it waits in place, and erasing with it (LOAD-01) */}
							{held === undefined && !unheld ? <Waiting label={text('holdings.reading')} language={language} kind="lines" rows={2} frame={false} /> : null}
							{unanswered && held === undefined ? <EmptyState kind="outage" frame={false} labels={EmptyState.labels[language]} /> : null}
							{unheld && !unanswered ? <Problem error={unheld} /> : null}
							{held?.blocking.length ? (
								<Callout tone="warning" live title={text('data.erase.blocking')}>
									<ul className="list-disc pl-5">
										{held.blocking.map(organization => (
											<li key={organization.id}>
												<Link className="underline" to={`/organizations/${encodeURIComponent(organization.id)}?view=members`}>
													{organization.name}
												</Link>
											</li>
										))}
									</ul>
								</Callout>
							) : null}
							<Consequence
								parts={{
									...(held && lines(held.holdings, products, text).length ? { affected: lines(held.holdings, products, text) } : {}),
									lost: [text('data.erase.lost.identity'), text('data.erase.lost.sessions'), text('data.erase.lost.memberships'), text('data.erase.lost.connections')],
									kept: text('data.erase.kept'),
									recovery: text('data.erase.recovery')
								}}
								labels={Consequence.labels[language]}
							/>
							<Field label={text('data.erase.confirm', { word })}>
								<input ref={confirmation} value={typed} autoComplete="off" onChange={event => setTyped(event.target.value)} />
							</Field>
							<div className="flex flex-wrap gap-2">
								<Button
									type="submit"
									variant="danger"
									busy={busy}
									labels={{ busy: text('data.erase.working') }}
									// The word not typed yet and what the products hold still read are steps to come: the
									// button waits quietly and a press puts the caret in the word (D183); only a sole
									// ownership that blocks the deletion turns it off
									waiting={typed.trim().toUpperCase() !== word.toUpperCase() || (held === undefined && !unheld)}
									place={() => confirmation.current}
									disabled={Boolean(held?.blocking.length)}
								>
									{text('data.erase.submit')}
								</Button>
								<Button type="button" variant="quiet" disabled={busy} onClick={() => setAsking(false)}>
									{text('action.cancel')}
								</Button>
							</div>
							<Unsent shown={unsent} />
						</form>
					) : (
						<div ref={opener}>
							<Button variant="danger" onClick={() => setAsking(true)}>
								{text('data.erase')}
							</Button>
						</div>
					)}
				</div>
			</div>
		</Panel>
	);
}
